import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dismissHeld, dismissNewVersion, reloadHeld, useLiveState } from '@/lib/live';

/**
 * Top-center bars of the live updates (lib/live.ts): someone else changed
 * the form you're editing, or a new dashboard version is out. Both only
 * offer "Neu laden" — nothing reloads by itself.
 */
export function LiveBanners() {
  const qc = useQueryClient();
  const live = useLiveState();
  if (!live.held && !live.newVersion) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-3">
      {live.held && (
        <Bar
          icon={<RefreshCw className="h-4 w-4 shrink-0 text-primary" />}
          action="Neu laden"
          onAction={() => reloadHeld(qc)}
          onClose={dismissHeld}
        >
          Wurde gerade {live.held.by.join(' und ')} geändert. Deine ungespeicherten Eingaben bleiben, bis du neu lädst.
        </Bar>
      )}
      {live.newVersion && (
        <Bar
          icon={<Sparkles className="h-4 w-4 shrink-0 text-primary" />}
          action="Neu laden"
          onAction={() => {
            if (!live.dirty || confirm('Ungespeicherte Eingaben auf dieser Seite gehen verloren. Trotzdem neu laden?')) {
              window.location.reload();
            }
          }}
          onClose={dismissNewVersion}
        >
          Neue Dashboard-Version verfügbar.
        </Bar>
      )}
    </div>
  );
}

function Bar({
  icon,
  action,
  onAction,
  onClose,
  children,
}: {
  icon: ReactNode;
  action: string;
  onAction: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className="glass-float pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-md border bg-card px-3 py-2 text-sm shadow-lg"
    >
      {icon}
      <span className="min-w-0 flex-1">{children}</span>
      <Button size="sm" onClick={onAction} className="shrink-0 gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" /> {action}
      </Button>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label="Ausblenden"
        title="Ausblenden"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
